import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import CategoryCard, { categoryHref, MEDIA_CARD_CLASS, MEDIA_CARD_GUTTER, MEDIA_CARD_GRID_3 } from "../components/CategoryCard";
import CategorySeo from "../components/CategorySeo";
import categoryBg from "../assets/category-bg.jpg";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const SKELETON_COUNT = 6;

function matchesQuery(item, needle) {
  const name = String(item.categoryName || "").toLowerCase();
  const description = String(item.description || "").toLowerCase();
  const slug = String(item.slug || "").toLowerCase();
  const tags = Array.isArray(item.searchTags)
    ? item.searchTags.join(" ").toLowerCase()
    : String(item.searchTags || item.tags || "").toLowerCase();
  return name.includes(needle) || description.includes(needle) || slug.includes(needle) || tags.includes(needle);
}

function categoryImageAlt(item, siteName) {
  const existing = String(item?.categoryPhotoAlt || "").trim();
  if (existing) return existing;
  const name = String(item?.categoryName || "").trim();
  if (name && siteName) return `${name} products supplied by ${siteName} Malaysia`;
  if (name) return `${name} products`;
  return "";
}

function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [contact, setContact] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("loading");

  const loadCategories = useCallback(() => {
    let cancelled = false;
    setStatus("loading");
    fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load categories");
        return res.json();
      })
      .then((list) => {
        if (cancelled) return;
        const published = (Array.isArray(list) ? list : [])
          .filter((item) => item?.isPublished !== false)
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
        setCategories(published);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([]);
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadCategories(), [loadCategories]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return categories;
    return categories.filter((item) => matchesQuery(item, needle));
  }, [categories, query]);

  useEffect(() => {
    const nodes = document.querySelectorAll("[data-reveal]");
    if (!nodes.length) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [filtered.length, status]);

  const siteName = String(contact?.siteName || "").trim();

  return (
    <div>
      <CategorySeo contact={contact} categories={status === "ready" ? categories : []} />
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={categoryBg}
          alt=""
          className="hero-zoom pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-y-0 left-0 h-full w-full bg-black/80 md:w-[52%]" />
        <div className="pointer-events-none absolute inset-y-0 left-[52%] hidden h-full w-28 bg-gradient-to-r from-black/80 to-transparent md:block" />
        <div className="absolute bottom-5 right-16 z-10 hidden border-l-2 border-at-gold bg-black/90 px-5 py-3 text-left md:right-32 md:block lg:right-40">
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">Building</p>
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">Industry</p>
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">Together</p>
        </div>
        <div className={`relative z-10 flex min-h-[260px] flex-col justify-between py-10 md:min-h-[320px] md:py-12 ${PAGE_GUTTER}`}>
          <div className="max-w-xl">
            <p className="hero-in hero-in-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">Our Products</p>
            <h1 className="hero-in hero-in-1 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">Product Categories</h1>
            <p className="hero-in hero-in-2 mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Explore our complete range of steel, hardware and industrial supplies for your project needs.
            </p>
          </div>
          <nav aria-label="Breadcrumb" className="hero-in hero-in-3 mt-8 text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <span className="text-white">Product Categories</span>
          </nav>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={MEDIA_CARD_GUTTER}>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-serif text-3xl uppercase text-at-dark md:text-4xl">All Product Categories</h2>
              <p className="mt-2 text-sm text-neutral-500">
                Browse by category to find the materials and supplies you need.
              </p>
            </div>
            <label className="relative block w-full sm:max-w-xs">
              <span className="sr-only">Search categories</span>
              <svg
                viewBox="0 0 24 24"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3-3" />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search categories?"
                className={`category-search w-full rounded-full border border-neutral-200 bg-white py-2.5 pl-10 text-sm text-at-dark outline-none placeholder:text-neutral-400 focus:border-at-gold ${query ? "pr-10" : "pr-4"}`}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              ) : null}
            </label>
          </div>

          <div className="mt-8" aria-live="polite">
            {status === "loading" ? (
              <div className={MEDIA_CARD_GRID_3} aria-hidden="true">
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <div key={index} className={`bg-neutral-200 ${MEDIA_CARD_CLASS}`} />
                ))}
              </div>
            ) : null}

            {status === "error" ? (
              <div className="text-sm text-neutral-500">
                <p>Unable to load categories.</p>
                <button
                  type="button"
                  onClick={loadCategories}
                  className="mt-3 font-semibold text-at-dark underline decoration-at-gold underline-offset-4 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  Retry
                </button>
              </div>
            ) : null}

            {status === "ready" && filtered.length ? (
              <div className={MEDIA_CARD_GRID_3}>
                {filtered.map((item, index) => (
                  <CategoryCard
                    key={item._id || item.slug}
                    to={categoryHref(item)}
                    photo={publicMediaSrc(item.categoryPhoto)}
                    alt={categoryImageAlt(item, siteName)}
                    title={item.categoryName}
                    description={item.description}
                    lazy={index >= 3}
                    revealIndex={index}
                    className={MEDIA_CARD_CLASS}
                  />
                ))}
              </div>
            ) : null}

            {status === "ready" && !filtered.length ? (
              <p className="text-sm text-neutral-500">
                {query.trim() ? "No categories found" : "No published categories yet."}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CategoriesPage;
